// Complete MemoryCarousel.tsx with smooth transitions
import React, { useState, useEffect, useRef } from 'react';
import { View, Heading, Text, Button, Loader } from '@aws-amplify/ui-react';
import { generateClient } from 'aws-amplify/api';
import { useNavigate } from 'react-router-dom';
import ProgressiveImage from './trip/ProgressiveImage';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { loadPhotoUrl, loadPhotoThumbnailUrl } from '../services/enhancedPhotoUrlService';
import './MemoryCarousel.css';

interface MemoryItem {
    id: string;
    tripId: string;
    tripName: string;
    photoId: string;
    uploadDate: string;
    thumbnailS3Key?: string;
    uploaderName?: string;
}
// Custom GraphQL query to get selected memories
const getUserMemories = /* GraphQL */ `
  query GetUserMemories($limit: Int) {
    getUserMemories(limit: $limit) {
      items {
        id
        tripId
        tripName
        photoId
        uploadDate
        thumbnailS3Key
        uploaderName
      }
    }
  }
`;

const MemoryCarousel: React.FC = () => {
    const [memories, setMemories] = useState<MemoryItem[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [autoplayPaused, setAutoplayPaused] = useState(false);
    const fadeTimeout = useRef<NodeJS.Timeout | null>(null);
    const preloadQueue = useRef<string[]>([]);

    const navigate = useNavigate();

    // Load memories
    useEffect(() => {
        (async () => {
            await fetchMemories();
        })();
    }, []);

    // Crossfade effect with preloading
    useEffect(() => {
        if (memories.length === 0 || autoplayPaused) return;

        if (fadeTimeout.current) {
            clearTimeout(fadeTimeout.current);
        }

        // Preload the next image when current one is displayed
        const nextIndex = (currentIndex + 1) % memories.length;
        if (memories[nextIndex]) {
            preloadPhotoWithThumbnail(memories[nextIndex].photoId);
        }

        fadeTimeout.current = setTimeout(() => {
            setCurrentIndex(nextIndex);
        }, 8000);

        return () => {
            if (fadeTimeout.current) {
                clearTimeout(fadeTimeout.current);
            }
        };
    }, [currentIndex, memories.length, autoplayPaused]);

    const fetchMemories = async () => {
        try {
            setLoading(true);
            const client = generateClient();

            const result: any = await client.graphql({
                query: getUserMemories,
                variables: { limit: 25 },
                authMode: "userPool"
            });

            const items = result.data.getUserMemories.items || [];
            setMemories(items);

            // Preload first few images
            if (items.length > 0) {
                // Queue up preloading first 3 images for smoother initial experience
                preloadQueue.current = items.slice(0, Math.min(3, items.length)).map((item: MemoryItem) => item.photoId);
                preloadNextInQueue();
            }
        } catch (error) {
            console.error('Error fetching memories:', error);
        } finally {
            setLoading(false);
        }
    };

    // Preload both thumbnail and full image
    const preloadPhotoWithThumbnail = async (photoId: string) => {
        try {
            // Load thumbnail first for quick display
            await loadPhotoThumbnailUrl(photoId, true);
            // Then start loading full image
            await loadPhotoUrl(photoId, false, true);
        } catch (error) {
            console.error('Error preloading image:', error);
        }
    };

    // Process preload queue
    const preloadNextInQueue = async () => {
        if (preloadQueue.current.length === 0) return;

        const nextId = preloadQueue.current.shift();
        if (nextId) {
            await preloadPhotoWithThumbnail(nextId);
            preloadNextInQueue(); // Continue with next in queue
        }
    };

    const handleNavigation = (direction: number) => {
        // Pause autoplay when user interacts
        setAutoplayPaused(true);

        // Calculate new index with wrap-around
        const newIndex = (currentIndex + direction + memories.length) % memories.length;
        setCurrentIndex(newIndex);

        // Preload the next image in the direction of navigation
        const nextIndex = (newIndex + direction + memories.length) % memories.length;
        if (memories[nextIndex]) {
            preloadPhotoWithThumbnail(memories[nextIndex].photoId);
        }
    };

    const handleIndicatorClick = (index: number) => {
        // Pause autoplay when user interacts
        setAutoplayPaused(true);
        setCurrentIndex(index);

        // Preload the next image after the one clicked
        const nextIndex = (index + 1) % memories.length;
        if (memories[nextIndex]) {
            preloadPhotoWithThumbnail(memories[nextIndex].photoId);
        }
    };

    // Format date for display - "2 years ago" style
    const formatDate = (dateString: string) => {
        try {
            const uploadDate = new Date(dateString);
            const now = new Date();
            const diffYears = now.getFullYear() - uploadDate.getFullYear();
            const diffMonths = now.getMonth() - uploadDate.getMonth();
            const diffDays = Math.floor((now.getTime() - uploadDate.getTime()) / (1000 * 60 * 60 * 24));

            if (diffYears > 1) return `${diffYears} years ago`;
            if (diffYears === 1) return '1 year ago';
            if (diffMonths > 1) return `${diffMonths} months ago`;
            if (diffMonths === 1) return '1 month ago';
            if (diffDays > 1) return `${diffDays} days ago`;
            if (diffDays === 1) return 'Yesterday';
            return 'Today';
        } catch (e) {
            return '';
        }
    };

    if (loading) {
        return (
            <View className="memories-section">
                <Heading level={2} className="section-heading">Memories</Heading>
                <View padding="2rem" textAlign="center">
                    <Loader size="large" />
                </View>
            </View>
        );
    }

    if (memories.length === 0) {
        return (
            <View className="memories-section">
                <Heading level={2} className="section-heading">Memories</Heading>
                <View padding="2rem" textAlign="center" backgroundColor="#1D1D1D" borderRadius="8px">
                    <Text color="#AAAAAA">No memories to display yet. Start by adding photos to your albums.</Text>
                </View>
            </View>
        );
    }

    return (
        <View className="memories-section">
            <Heading level={2} className="section-heading">Memories</Heading>

            <div className="memory-carousel">
                <div className="memory-slides-container">
                    {memories.map((memory, index) => (
                        <div
                            key={memory.photoId}
                            className="memory-slide"
                            style={{
                                opacity: currentIndex === index ? 1 : 0,
                                zIndex: currentIndex === index ? 2 : 1,
                                pointerEvents: currentIndex === index ? 'auto' : 'none'
                            }}
                            onClick={() => navigate(`/trips/${memory.tripId}`)}
                        >
                            <ProgressiveImage
                                photoId={memory.photoId}
                                alt={memory.tripName || 'Memory'}
                                width="100%"
                                height="100%"
                                objectFit="cover"
                                loadFullImage={
                                    currentIndex === index ||
                                    index === (currentIndex + 1) % memories.length
                                }
                                useBlurTransition={true}
                            />

                            {/* Date overlay */}
                            <div className="memory-date-badge">
                                <Text>{formatDate(memory.uploadDate)}</Text>
                            </div>

                            {/* Info section */}
                            <div className="memory-info">
                                <Text className="memory-caption">
                                    {memory.tripName}
                                </Text>
                                <Text className="memory-details">
                                    Captured by {memory.uploaderName || 'a friend'}
                                </Text>
                            </div>
                        </div>
                    ))}

                    {/* Navigation arrows */}
                    <Button
                        className="memory-nav-arrow memory-nav-prev"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleNavigation(-1);
                        }}
                        aria-label="Previous memory"
                    >
                        <ChevronLeft size={24} />
                    </Button>

                    <Button
                        className="memory-nav-arrow memory-nav-next"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleNavigation(1);
                        }}
                        aria-label="Next memory"
                    >
                        <ChevronRight size={24} />
                    </Button>
                </div>

                {/* Navigation dots */}
                <div className="memory-indicators">
                    {memories.map((_, index) => (
                        <button
                            key={index}
                            className={`indicator ${currentIndex === index ? 'active' : ''}`}
                            onClick={() => handleIndicatorClick(index)}
                            aria-label={`Go to memory ${index + 1}`}
                        />
                    ))}
                </div>
            </div>
        </View>
    );
};

export default MemoryCarousel;